import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';

export interface VyaparPaymentRequest {
  amount: number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  order_id?: string;
}

export interface VyaparPaymentResponse {
  success: boolean;
  payment_url?: string; // UPI intent URL for mobile
  qr_code?: string; // QR payload for desktop
  order_id?: string; // Intent ID
  qr_url?: string; // Pre-rendered QR image URL
  error?: string;
}

export interface VyaparVerificationRequest {
  intent_id: string;
}

export interface VyaparVerificationResponse {
  success: boolean;
  verified: boolean;
  payment_details?: any;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VyaparGatewayService {
  private readonly FIREBASE_FUNCTIONS_BASE = `https://${environment.firebaseFunctions.region}-${environment.firebaseFunctions.projectId}.cloudfunctions.net`;

  constructor(private http: HttpClient, private toastService: ToastService) {}

  /**
   * Create VyaparGateway order
   */
  createOrder(paymentRequest: VyaparPaymentRequest): Observable<VyaparPaymentResponse> {
    console.log('🔑 Creating VyaparGateway order');
    
    if (!this.validatePaymentRequest(paymentRequest)) {
      return throwError(() => new Error('Invalid payment request'));
    }

    const endpoint = `${this.FIREBASE_FUNCTIONS_BASE}/createVyaparOrder`;
    console.log('📡 Calling VyaparGateway Firebase Function:', endpoint);

    return this.http.post<VyaparPaymentResponse>(endpoint, paymentRequest).pipe(
      map(response => {
        if (response?.success && (response.payment_url || response.qr_code)) {
          console.log('✅ VyaparGateway order created:', response.order_id);
          return response;
        }
        console.error('❌ VyaparGateway order creation failed:', response);
        throw new Error(response?.error || 'Failed to create payment order');
      }),
      catchError(error => {
        console.error('❌ VyaparGateway order creation error:', error);
        const errorMessage = error?.error?.error || error?.message || 'Failed to create payment order';
        this.toastService.error(errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Detect if user is on mobile device
   */
  isMobileDevice(): boolean {
    if (typeof window === 'undefined') return false;
    
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Mobile device detection patterns
    const mobilePatterns = [
      /Android/i,
      /webOS/i,
      /iPhone/i,
      /iPad/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i,
      /IEMobile/i
    ];

    return mobilePatterns.some(pattern => pattern.test(userAgent));
  }

  /**
   * Initiate VyaparGateway payment
   */
  initiatePayment(paymentResponse: VyaparPaymentResponse): Observable<VyaparPaymentResponse> {
    return new Observable(observer => {
      if (this.isMobileDevice()) {
        // Mobile: Open UPI app via intent
        if (paymentResponse.payment_url) {
          console.log('📱 Opening UPI app via intent');
          window.location.href = paymentResponse.payment_url;
          
          // Poll for payment completion
          this.pollPaymentStatus(paymentResponse.order_id!).subscribe({
            next: (status) => {
              if (status.verified) {
                observer.next({
                  success: true,
                  order_id: paymentResponse.order_id,
                  status: 'success'
                } as any);
                observer.complete();
              }
            },
            error: (error) => {
              observer.error(error);
            }
          });
        } else {
          observer.error(new Error('Payment URL not available for mobile'));
        }
      } else {
        // Desktop: Return QR code for display
        console.log('💻 Desktop: Returning QR code');
        observer.next({
          success: true,
          qr_code: paymentResponse.qr_code,
          order_id: paymentResponse.order_id,
          requires_qr_display: true
        } as any);
        observer.complete();
      }
    });
  }

  /**
   * Poll for payment status (for mobile UPI intent)
   */
  private pollPaymentStatus(orderId: string): Observable<VyaparVerificationResponse> {
    return new Observable(observer => {
      const maxAttempts = 30; // 30 attempts = 5 minutes (10 seconds each)
      let attempts = 0;

      const pollInterval = setInterval(() => {
        attempts++;
        
        this.verifyPayment(orderId).subscribe({
          next: (response) => {
            if (response.verified) {
              clearInterval(pollInterval);
              observer.next(response);
              observer.complete();
            } else if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              observer.error(new Error('Payment verification timeout'));
            }
          },
          error: (error) => {
            if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              observer.error(error);
            }
          }
        });
      }, 10000); // Poll every 10 seconds
    });
  }

  /**
   * Verify VyaparGateway payment
   */
  verifyPayment(intentId: string): Observable<VyaparVerificationResponse> {
    if (!intentId) {
      return throwError(() => new Error('Missing required parameters for payment verification'));
    }

    const verificationRequest: VyaparVerificationRequest = {
      intent_id: intentId
    };

    const endpoint = `${this.FIREBASE_FUNCTIONS_BASE}/verifyVyaparPayment`;

    return this.http.post<VyaparVerificationResponse>(endpoint, verificationRequest).pipe(
      map(response => {
        if (response?.success) {
          return response;
        }
        return response;
      }),
      catchError(error => {
        const errorMessage = error?.error?.error || error?.message || 'Payment verification failed';
        return of({
          success: false,
          verified: false,
          error: errorMessage
        });
      })
    );
  }

  /**
   * Validate payment request
   */
  private validatePaymentRequest(paymentRequest: VyaparPaymentRequest): boolean {
    if (!paymentRequest.amount || paymentRequest.amount <= 0) {
      this.toastService.error('Invalid payment amount');
      return false;
    }

    if (!paymentRequest.customer_name) {
      this.toastService.error('Customer name is required');
      return false;
    }

    if (!paymentRequest.customer_phone) {
      this.toastService.error('Customer phone is required');
      return false;
    }

    return true;
  }

  /**
   * Validate phone number
   */
  validatePhoneNumber(phone: string): boolean {
    return /^[6-9]\d{9}$/.test(phone);
  }

  /**
   * Validate email
   */
  validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Validate payment amount
   */
  validateAmount(amount: number): boolean {
    return amount > 0 && amount <= 1000000; // Max 10 lakhs
  }

  /**
   * Generate order ID
   */
  generateOrderId(): string {
    return 'vyapar_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Format amount for display
   */
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }
}
